import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  fetchLmsFamilies,
  fetchMyMarketPreferences,
  putMyMarketPreferences,
} from '@/lib/marketExperienceApi';
import {
  countriesPayload,
  countryToQueryParam,
  lmsToQueryParam,
  parseLmsQueryParam,
  resolveCountryFilter,
} from '@/lib/marketUserFilters';

function urlOrNull(searchParams, key) {
  return searchParams.has(key) ? (searchParams.get(key) || '') : null;
}

export function useMarketUserFilters(tenantId, { enabled = true } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [prefs, setPrefs] = useState(null);
  const [lmsFamilies, setLmsFamilies] = useState([]);
  const [ready, setReady] = useState(false);
  const [country, setCountryState] = useState('all');
  const [lmsVendors, setLmsVendorsState] = useState([]);

  const urlCountry = urlOrNull(searchParams, 'country');
  const urlLms = urlOrNull(searchParams, 'lms');

  useEffect(() => {
    if (!enabled || !tenantId) {
      setPrefs(null);
      setLmsFamilies([]);
      setReady(false);
      return undefined;
    }
    let cancelled = false;
    setReady(false);
    fetchMyMarketPreferences(tenantId)
      .catch(() => null)
      .then((nextPrefs) => {
        if (cancelled) return;
        setPrefs(nextPrefs);
        setCountryState(resolveCountryFilter(urlCountry, nextPrefs?.countries));
        if (urlLms !== null) {
          setLmsVendorsState(parseLmsQueryParam(urlLms) || []);
        } else {
          setLmsVendorsState(Array.isArray(nextPrefs?.lms_vendors) ? nextPrefs.lms_vendors.filter(Boolean) : []);
        }
        setReady(true);
      });
    return () => { cancelled = true; };
    // URL is applied once prefs load; later URL edits go through setters.
    // Do not key this effect on country — it re-resolves country from prefs and would clobber selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, tenantId]);

  useEffect(() => {
    if (!enabled || !tenantId || !ready) return undefined;
    let cancelled = false;
    fetchLmsFamilies(tenantId, countryToQueryParam(country))
      .then((familiesPayload) => {
        if (cancelled) return;
        setLmsFamilies(Array.isArray(familiesPayload?.families) ? familiesPayload.families : []);
      })
      .catch(() => {
        if (!cancelled) setLmsFamilies([]);
      });
    return () => { cancelled = true; };
  }, [enabled, tenantId, ready, country]);

  const patchUrl = useCallback((key, value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(key, value);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setCountry = useCallback((nextCountry) => {
    const value = nextCountry || 'all';
    setCountryState(value);
    patchUrl('country', countryToQueryParam(value));
    if (!tenantId) return;
    putMyMarketPreferences(tenantId, { countries: countriesPayload(value) }).catch(() => {});
  }, [patchUrl, tenantId]);

  const setLmsVendors = useCallback((nextVendors) => {
    const list = Array.isArray(nextVendors) ? nextVendors.filter(Boolean) : [];
    setLmsVendorsState(list);
    patchUrl('lms', lmsToQueryParam(list));
    if (!tenantId) return;
    putMyMarketPreferences(tenantId, { lms_vendors: list }).catch(() => {});
  }, [patchUrl, tenantId]);

  const countryOptions = useMemo(() => {
    const fromPrefs = Array.isArray(prefs?.available_countries) ? prefs.available_countries.filter(Boolean) : [];
    return fromPrefs;
  }, [prefs]);

  const countryParam = countryToQueryParam(country);
  const lmsParam = lmsToQueryParam(lmsVendors);

  return {
    ready,
    country,
    setCountry,
    lmsVendors,
    setLmsVendors,
    lmsFamilies,
    countryOptions,
    countryParam,
    lmsParam,
    prefs,
  };
}
